import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';

import Meta from '../../components/Meta';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import Data from '../../../repository';
import styles from '../../../styles/Admin.module.scss';

const formatMoney = (amount) => `£${(Number(amount || 0) / 100).toFixed(2)}`;

const TX_TYPE_LABELS = {
	purchase: 'Top Up',
	spend: 'Spend',
	refund: 'Refund',
	adjustment: 'Adjustment',
	membership_grant: 'Membership Grant',
};

function formatDate(date) {
	if (!date) return '—';
	return new Date(date).toLocaleString('en-GB', {
		day: '2-digit', month: 'short', year: 'numeric',
		hour: '2-digit', minute: '2-digit'
	});
}

function PendingWalletPayments() {
	const [loading, setLoading] = useState(true);
	const [pendingTransactions, setPendingTransactions] = useState([]);
	const [completing, setCompleting] = useState(null);
	const [completeError, setCompleteError] = useState(null);

	useEffect(() => {
		load();
	}, []);

	const load = async () => {
		setLoading(true);
		try {
			const result = await Data.adminFetchAllPendingWalletTransactions();
			setPendingTransactions(result.data || []);
		} finally {
			setLoading(false);
		}
	};

	const completeTransaction = async (transactionId) => {
		setCompleting(transactionId);
		setCompleteError(null);
		try {
			await Data.adminCompleteWalletTransaction(transactionId);
			await load();
		} catch (err) {
			setCompleteError(err.response?.data?.message || 'Failed to complete transaction.');
		} finally {
			setCompleting(null);
		}
	};

	if (loading) return <Loading button={false} />;

	return (
		<>
			{completeError ? <p className={styles.error}>{completeError}</p> : null}

			{!pendingTransactions.length ? (
				<p>No pending wallet payments.</p>
			) : (
				<div className={styles.tableWrap}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>User</th>
								<th>Package</th>
								<th>Amount</th>
								<th>Date</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{pendingTransactions.map((tx) => (
								<tr key={tx._id}>
									<td>
										{tx.User ? (
											<Link to={`/admin/users/${tx.User._id}`} className={styles.tableLink}>
												{tx.User.firstName} {tx.User.lastName}
											</Link>
										) : '—'}
										{tx.User?.email ? <p>{tx.User.email}</p> : null}
									</td>
									<td>{tx.WalletPackage?.name || '—'}</td>
									<td>{formatMoney(tx.amount)}</td>
									<td>{formatDate(tx.createdAt)}</td>
									<td className={styles.tableActions}>
										<button
											type="button"
											className="btn btnPrimary"
											disabled={completing === tx._id}
											onClick={() => completeTransaction(tx._id)}
										>
											{completing === tx._id ? 'Completing...' : 'Mark Complete'}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
}

function AllWalletTransactions() {
	const [loading, setLoading] = useState(true);
	const [transactions, setTransactions] = useState([]);
	const [error, setError] = useState(null);
	const [exporting, setExporting] = useState(false);

	useEffect(() => {
		load();
	}, []);

	const load = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await Data.fetchWalletTransactions();
			const sorted = (result.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
			setTransactions(sorted);
		} catch (err) {
			setError(err.response?.data?.message || 'Failed to load wallet transactions.');
		} finally {
			setLoading(false);
		}
	};

	const exportCsv = async () => {
		setExporting(true);
		try {
			const response = await Data.exportWalletTransactions();
			const blob = new Blob([response.data], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'wallet-transactions.csv');
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			window.URL.revokeObjectURL(url);
		} finally {
			setExporting(false);
		}
	};

	if (loading) return <Loading button={false} />;

	if (error) return <p className={styles.error}>{error}</p>;

	if (!transactions.length) return <p>No wallet transactions found.</p>;

	return (
		<div>
			<button type="button" className="btn btnPrimary" disabled={exporting} onClick={exportCsv} style={{ marginBottom: '16px' }}>
				{exporting ? 'Exporting...' : 'Export CSV'}
			</button>
			<div className={styles.tableWrap}>
				<table className={styles.table}>
					<thead>
						<tr>
							<th>User</th>
							<th>Type</th>
							<th>Amount</th>
							<th>Status</th>
							<th>Detail</th>
							<th>Date</th>
						</tr>
					</thead>
					<tbody>
						{transactions.map((tx) => (
							<tr key={tx._id}>
								<td>
									{tx.User ? (
										<Link to={`/admin/users/${tx.User._id}`} className={styles.tableLink}>
											{tx.User.firstName} {tx.User.lastName}
										</Link>
									) : '—'}
								</td>
								<td>{TX_TYPE_LABELS[tx.type] || tx.type}</td>
								<td style={{ color: Number(tx.amount) < 0 ? '#c0392b' : 'inherit' }}>
									{Number(tx.amount) >= 0 ? '+' : ''}{formatMoney(tx.amount)}
								</td>
								<td>{tx.status}</td>
								<td>
									{tx.WalletPackage?.name || ''}
									{tx.ExperienceBooking ? 'Class booking' : ''}
									{tx.ServiceBooking ? 'Service booking' : ''}
									{tx.Booking ? 'Session booking' : ''}
									{tx.note ? tx.note : ''}
								</td>
								<td>{formatDate(tx.createdAt)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function WalletTransactions() {
	const [view, setView] = useState('pending');

	return (
		<>
			<div className={styles.tabs}>
				<button
					type="button"
					className={`${styles.tab} ${view === 'pending' ? styles.active : ''}`}
					onClick={() => setView('pending')}
				>
					Pending
				</button>
				<button
					type="button"
					className={`${styles.tab} ${view === 'all' ? styles.active : ''}`}
					onClick={() => setView('all')}
				>
					All Transactions
				</button>
			</div>

			<div className={styles.tabContent}>
				{view === 'pending' ? <PendingWalletPayments /> : <AllWalletTransactions />}
			</div>
		</>
	);
}

function Template() {
	return (
		<>
			<Meta title="Wallet Transactions" />
			<Header heading="Wallet Transactions" />
			<main>
				<Outlet />
			</main>
		</>
	);
}

export default { Template, List: WalletTransactions };
